/**
 * The Deathmoth. Drawn to light, and therefore to whoever is carrying it.
 *
 * It is the only entity in the game that makes the flashlight a liability, and
 * that is its whole job. `perception.sightSignal` already scales an entity's
 * sight range by how lit the player is, and treats a lit torch as 0.9 whatever
 * the room is doing; with a 42 m base range that means a moth finds a player
 * with the light on from nearly forty metres and one with it off from eight.
 * So the counterplay is a button the player already has, and pressing it works
 * mid-chase — it forgets in under two seconds and peels off to the nearest
 * ceiling fixture, which is where it spends its time when nobody is helping.
 *
 * In exchange for being solvable it is relentless and quick. It does not creep
 * up on anything or hold a band; it commits at very low awareness, flies at
 * five metres a second and hits for seven every three quarters of a second.
 * A player who insists on keeping the torch on while one is up will lose the
 * argument slowly and entirely fairly.
 *
 * The flight weaves rather than tracking straight, which is what makes 5.0 m/s
 * survivable against a 5.0 m/s sprint: the path it actually covers is longer
 * than the line, so a fleeing player gains a little ground per second while a
 * standing one gets caught immediately.
 */

import { clamp01 } from '../../core/math';
import type { Vec2 } from '../../core/types';
import {
  brightestNear,
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal, sightSignal } from '../perception';
import { VOICE } from '../voice';

const T = ENTITY_TUNING.deathmoth;

/** Reused steering target. One allocation for the life of the module. */
const SCRATCH: Vec2 = { x: 0, z: 0 };

/**
 * How lit the player is, from any source. Drives whether it *commits* once it
 * is near enough to see them at all.
 */
function beacon(w: EntityWorld): number {
  return Math.max(w.playerLight, w.player.flashlightOn ? 0.9 : 0);
}

/**
 * Whether the player is worth crossing the level for, which is a narrower
 * question and deliberately a different one.
 *
 * A moth goes to lights. A player standing in a lit room is not a light — the
 * room is, and there is one of those every twenty metres, so a moth that
 * crossed the floor for ambient brightness would simply always arrive and the
 * flashlight would stop being the thing that matters. A player *carrying* a
 * torch is a light that moves, and that one it will follow anywhere.
 *
 * So the room's own brightness gets a moth to notice you once it is close (see
 * `beacon`, which feeds `sightSignal`), and only the torch gets it to come.
 * Which is also what the wiki's survival advice says in the other direction:
 * extinguish your lights, and avoid well-lit areas.
 */
function carried(w: EntityWorld): boolean {
  return w.player.flashlightOn && w.player.flashlightCharge > 0;
}

/**
 * A point offset sideways from `target`, swinging back and forth. The offset
 * shrinks to nothing on the approach so the last two metres are a straight line
 * and the strike is not a near miss forever.
 */
function weaveTo(a: Agent, w: EntityWorld, target: Vec2): Vec2 {
  const dx = target.x - a.state.pos.x;
  const dz = target.z - a.state.pos.z;
  const d = Math.hypot(dx, dz);
  if (d < 1e-3) return target;
  const swing = Math.sin(a.state.age * T.weaveRate) * T.weaveAmplitude * clamp01((d - 1.5) / 6);
  // Perpendicular in the XZ plane.
  SCRATCH.x = target.x + (-dz / d) * swing;
  SCRATCH.z = target.z + (dx / d) * swing;
  return SCRATCH;
}

export const deathmothBehavior: Behavior = {
  kind: 'deathmoth',

  spawn(a, w) {
    a.turnRate = 6.5;
    a.timer = a.rng.range(T.wingInterval[0], T.wingInterval[1]);
    a.timer2 = 0;
    // It starts where the light is. A moth found standing in a dark corridor is
    // a moth the player has no way of understanding.
    const lamp = brightestNear(w, a.state.pos, T.lampRadius);
    if (lamp) {
      a.anchor.x = lamp.x;
      a.anchor.z = lamp.z;
    } else {
      a.anchor.x = a.state.pos.x;
      a.anchor.z = a.state.pos.z;
    }
    a.dest.x = a.anchor.x;
    a.dest.z = a.anchor.z;
  },

  think(a, w) {
    const s = a.state;
    const lit = beacon(w);

    // Below the gate the player is simply not a light source and nothing about
    // them registers. Above it, sight does all the work — the hearing term is
    // there so that walking into one in the dark still wakes it up.
    const signal =
      lit >= T.lightGate
        ? Math.max(sightSignal(a.def, s, w.grid, w.player, w.playerLight), hearSignal(a.def, s, w.grid, w.player))
        : hearSignal(a.def, s, w.grid, w.player);
    driveAwareness(a, w, signal, T.gain, T.forget);

    // Wings. Continuous while it is moving, faster as it closes, and the only
    // warning that one is on its way when the player's own light is off.
    if (s.mode !== 'dormant') {
      a.timer -= w.dt;
      if (a.timer <= 0) {
        const close = clamp01(1 - s.distToPlayer / 20);
        a.timer = a.rng.range(T.wingInterval[0], T.wingInterval[1]) * (1 - 0.4 * close);
        w.audio.play(VOICE.mothWings, { pos: s.pos, gain: 0.28 + 0.3 * close, rate: 1.65 + 0.25 * close });
      }
    }

    switch (s.mode) {
      case 'chase': {
        const target = s.lastKnown ?? w.player.pos;
        moveTo(a, w, weaveTo(a, w, target), a.def.chaseSpeed);
        if (s.distToPlayer <= a.def.attackRange) {
          setMode(a, w, 'attack');
          break;
        }
        // The torch went out. It gives up quickly and audibly by simply
        // turning away, which is the lesson: the light was the reason.
        if (s.awareness < 0.08) setMode(a, w, 'wander');
        break;
      }

      case 'attack': {
        faceToward(a, w.player.pos);
        holdStill(a);
        tryAttack(a, w);
        w.dread(s, a.def.sanityDrain);
        if (s.distToPlayer > a.def.attackRange * 1.5) setMode(a, w, 'chase');
        break;
      }

      default: {
        setMode(a, w, 'wander');

        if (carried(w)) {
          // There is a torch on out there. It goes to it — across the level if
          // need be, without needing to see the player first, because that is
          // what a moth does and it is the reason this one is allowed to hold a
          // pursuit licence at all. The sweep is handed the torch as its reason
          // rather than the player's footsteps: crouching does not hide you
          // from a moth, and the switch is the whole answer.
          sweepStep(a, w, a.def.walkSpeed * 1.7, true);
        } else {
          // Nothing worth flying at. It orbits a fixture instead, which is
          // exactly where the player goes to get their sanity back.
          a.timer2 -= w.dt;
          if (a.timer2 <= 0) {
            a.timer2 = a.rng.range(3.5, 7);
            const lamp = brightestNear(w, a.anchor, T.lampRadius);
            if (lamp) {
              a.dest.x = lamp.x;
              a.dest.z = lamp.z;
              // The anchor creeps toward whatever it found, so a moth with no
              // fixture near it drifts until it has one.
              a.anchor.x += (lamp.x - a.anchor.x) * 0.5;
              a.anchor.z += (lamp.z - a.anchor.z) * 0.5;
            }
          }
          moveTo(a, w, a.dest, a.def.walkSpeed);
        }

        if (s.awareness >= T.commitAwareness && lit >= T.lightGate) setMode(a, w, 'chase');
        break;
      }
    }
  },
};
